/** Date helpers. Every one of these is deliberately local-time only.
 *
 *  NEVER use toISOString() to build a date key here. It converts to UTC, which
 *  shifts the day for anyone west of Greenwich in the evening. This actually
 *  happened during prototyping: every date lookup missed and the whole meetings
 *  list rendered as gaps, and a Sunday displayed as the following Monday.
 *
 *  Dates are parsed at noon so that daylight-saving transitions — which happen
 *  around 2am — can never push a date onto the neighbouring day.
 */

/** Mean days per month (365.25 / 12). Used for the month-based settings so a
 *  "6 mo" threshold doesn't drift with the calendar. */
export const AVG_MONTH_DAYS = 30.44;

const pad = (n: number) => String(n).padStart(2, "0");

/** Today, at noon local. A fresh object each call, so callers may mutate it. */
export function today(): Date {
  const d = new Date();
  d.setHours(12, 0, 0, 0);
  return d;
}

/** Date -> "YYYY-MM-DD", using local calendar fields. */
export function iso(d: Date): string {
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/** "YYYY-MM-DD" -> Date at local noon. */
export function parseDate(s: string): Date {
  const [y, m, d] = s.split("-").map(Number);
  return new Date(y, (m ?? 1) - 1, d ?? 1, 12, 0, 0, 0);
}

/** Whole days from `s` until today. Negative for future dates. */
export function daysSince(s: string): number {
  return Math.round((today().getTime() - parseDate(s).getTime()) / 86_400_000);
}

/** "Aug 16, 2026" */
export function fmtDate(s: string): string {
  return parseDate(s).toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

/** "Monday" — used to tell the clerk a date isn't a Sunday. */
export function weekdayOf(s: string): string {
  return parseDate(s).toLocaleDateString(undefined, { weekday: "long" });
}

export function isSunday(s: string): boolean {
  return parseDate(s).getDay() === 0;
}

/** Most recent Sunday, today included. The Record screen's default date. */
export function lastSunday(): string {
  const d = today();
  d.setDate(d.getDate() - d.getDay());
  return iso(d);
}

/** Human elapsed phrase. Coarsens as it goes — nobody needs "412 days ago". */
export function elapsed(s: string | null): string {
  if (!s) return "never";
  const d = daysSince(s);
  if (d <= 0) return "today";
  if (d < 14) return `${d}d ago`;
  if (d < 60) return `${Math.floor(d / 7)} wk ago`;
  if (d < 365) return `${Math.round(d / 30)} mo ago`;
  const y = Math.floor(d / 365);
  const m = Math.round((d % 365) / 30);
  return m >= 1 && m < 12 ? `${y} yr ${m} mo ago` : `${y} yr ago`;
}
